import { useRef, useState } from "react";
import { formatRunStart } from "../../engine/run-filename";
import { choosePair, type RunFile } from "./choose-pair";

/**
 * Drop two exports, or a folder of them.
 *
 * Two `<input type=file>` pickers were the only way in, which meant knowing
 * which of two files is the reference before the app had told you anything
 * about either. Dropping both and letting the filenames settle the direction is
 * the ordinary case; the pickers stay for everyone who prefers them.
 *
 * Directory drops are read through `webkitGetAsEntry`, which every browser this
 * app targets supports. The newer `getAsFileSystemHandle` would do the same job
 * with a nicer API and narrower support, and buys nothing here — we only need
 * the files, never a writable handle.
 */
export function FileDropZone({
  onPair,
  disabled = false
}: {
  onPair: (reference: File, candidate: File, orderedBy: "filename" | "arrival") => void;
  disabled?: boolean;
}) {
  const [over, setOver] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [picking, setPicking] = useState<RunFile[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const folderInput = useRef<HTMLInputElement | null>(null);

  const accept = (files: File[]) => {
    setMessage(null);
    setPicking(null);
    const choice = choosePair(files);
    if (choice.kind === "none") {
      setMessage(choice.reason);
      return;
    }
    if (choice.kind === "pair") {
      onPair(choice.reference, choice.candidate, choice.orderedBy);
      return;
    }
    // Several runs: offer the usual comparison, but let the person change it.
    setPicking(choice.runs);
    setChosen([choice.suggested.reference.name, choice.suggested.candidate.name]);
  };

  const onDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    accept(await filesFromDataTransfer(event.dataTransfer));
  };

  return (
    <section className="space-y-2">
      <div
        data-testid="file-drop-zone"
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => void onDrop(event)}
        className={`rounded border-2 border-dashed p-4 text-sm ${
          over ? "border-sky-500 bg-sky-50" : "border-slate-300 bg-white"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <p className="font-medium">Drop both exports here, or a folder of runs.</p>
        <p className="mt-1 text-xs text-slate-600">
          When the filenames carry the pipeline's run timestamps, the older run is used as the
          reference and the newer as the candidate. Otherwise they load in the order given and the
          export dates below decide — the swap control is always available.
        </p>
        <button
          type="button"
          className="mt-2 rounded-sm border border-slate-300 bg-white px-3 py-1 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
          data-testid="choose-folder"
          disabled={disabled}
          onClick={() => folderInput.current?.click()}
        >
          Choose a folder…
        </button>
        <input
          ref={folderInput}
          type="file"
          className="hidden"
          data-testid="folder-input"
          multiple
          // Non-standard but universally implemented; the React types do not
          // know it, hence the cast rather than a widened prop type.
          {...({ webkitdirectory: "" } as Record<string, string>)}
          onChange={(event) => accept([...(event.target.files ?? [])])}
        />
      </div>

      {message === null ? null : (
        <p className="rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="drop-message">
          {message}
        </p>
      )}

      {picking === null ? null : (
        <RunPicker
          runs={picking}
          chosen={chosen}
          onToggle={(name) =>
            setChosen((current) =>
              current.includes(name)
                ? current.filter((entry) => entry !== name)
                : [...current, name].slice(-2)
            )
          }
          onConfirm={() => {
            const files = chosen
              .map((name) => picking.find((entry) => entry.file.name === name))
              .filter((entry): entry is RunFile => entry !== undefined);
            if (files.length !== 2) return;
            // Re-derive the direction from the two chosen names rather than from
            // click order, so picking newest-then-oldest is not a reversed run.
            const choice = choosePair(files.map((entry) => entry.file));
            if (choice.kind === "pair") {
              onPair(choice.reference, choice.candidate, choice.orderedBy);
              setPicking(null);
            }
          }}
          onCancel={() => setPicking(null)}
        />
      )}
    </section>
  );
}

function RunPicker({
  runs,
  chosen,
  onToggle,
  onConfirm,
  onCancel
}: {
  runs: RunFile[];
  chosen: string[];
  onToggle: (name: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rounded-sm border border-slate-300 bg-white p-3" data-testid="run-picker">
      <p className="text-sm font-medium">
        {runs.length} runs in that folder. Which pair?
      </p>
      <p className="mt-1 text-xs text-slate-600">
        The newest run and the one before it are selected — the usual comparison. Pick two others if
        you meant a different pair.
      </p>
      <ul className="mt-2 space-y-1">
        {runs.map((entry) => (
          <li key={entry.file.name}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={`run-option-${entry.run.runId}`}
                checked={chosen.includes(entry.file.name)}
                onChange={() => onToggle(entry.file.name)}
              />
              <span className="font-mono text-xs">{formatRunStart(entry.run)}</span>
              <span className="truncate text-xs text-slate-600">{entry.file.name}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          className="rounded-sm bg-sky-700 px-3 py-1 text-sm text-white disabled:bg-slate-400"
          data-testid="confirm-run-pair"
          disabled={chosen.length !== 2}
          onClick={onConfirm}
        >
          Use these two
        </button>
        <button type="button" className="text-sm text-slate-600 underline" data-testid="cancel-run-pair" onClick={onCancel}>
          Cancel
        </button>
        <span className="text-xs text-slate-600">{chosen.length} of 2 selected</span>
      </div>
    </div>
  );
}

/**
 * The files behind a drop, following one level of directory.
 *
 * A folder drop yields directory entries rather than files, and reading them is
 * asynchronous, so the DataTransfer's items must be captured before the first
 * await — the list is emptied once the drop event finishes dispatching.
 */
async function filesFromDataTransfer(transfer: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  const plain: File[] = [];
  for (const item of [...(transfer.items ?? [])]) {
    if (item.kind !== "file") continue;
    const entry = typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null;
    if (entry !== null) {
      entries.push(entry);
      continue;
    }
    const file = item.getAsFile();
    if (file !== null) plain.push(file);
  }
  if (entries.length === 0 && plain.length === 0) return [...(transfer.files ?? [])];

  const collected: File[] = [...plain];
  for (const entry of entries) {
    collected.push(...(await readEntry(entry)));
  }
  return collected;
}

async function readEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) =>
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null))
    );
    return file === null ? [] : [file];
  }
  if (!entry.isDirectory) return [];

  // One level deep only: a run folder holds exports, not a tree of them, and
  // walking arbitrarily deep would turn a mis-drop into a long scan.
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const children = await new Promise<FileSystemEntry[]>((resolve) =>
    reader.readEntries(resolve, () => resolve([]))
  );
  const files: File[] = [];
  for (const child of children) {
    if (child.isFile) files.push(...(await readEntry(child)));
  }
  return files;
}
